// @flow

import * as React from 'react';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';

import { selectIsAuthenticatedUser } from '@gandi/authentication';

import { selectCurrentId } from '@gandi/redux-sharing';

import { useTranslator } from '@gandi/translate';

import { Button } from '@gandi/ui';

import { useQuery } from 'src/helpers/withRouter';
import type { Query } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';
import type { PreFetchArgs, PreFetchReturn, FullState } from 'src/types';

import { selectCountry, selectCurrency } from '../../modules/selectors';

import AnonymousDialog from './AnonymousDialog';

import AuthenticatedDialog from './AuthenticatedDialog';

import type { DialogState } from './types';

type Props = { dialog: DialogState };

export default function PreferencesWidget({
  dialog,
}: Props): React.MixedElement {
  const isAuthenticatedUser = useAppSelector(selectIsAuthenticatedUser);

  const { __, formatCurrency } = useTranslator();

  const { country: locationQueryCountry, currency: locationQueryCurrency } =
    useQuery<Query>();

  const currentId = useAppSelector(selectCurrentId);

  const buttonCurrencyTitle = __('Currency:');

  const countryProps = React.useMemo(
    () => ({
      locationQueryCountry,
      orgId: currentId,
    }),
    [locationQueryCountry, currentId],
  );

  const countryCode = useAppSelector(state =>
    selectCountry(state, countryProps),
  );

  const currencyProps = React.useMemo(
    () => ({
      locationQueryCurrency,
      country: countryCode,
      orgId: currentId,
    }),
    [locationQueryCurrency, countryCode, currentId],
  );

  const currency = useAppSelector(state =>
    selectCurrency(state, currencyProps),
  );
  const formattedCurrency = formatCurrency(currency);

  return (
    <>
      <Button
        kind="secondary"
        onClick={dialog.show}
        title={`${buttonCurrencyTitle} ${formattedCurrency} ${currency}`}
      >
        {`${formattedCurrency} ${currency}`}
      </Button>

      {isAuthenticatedUser ? (
        <AuthenticatedDialog dialogState={dialog} />
      ) : (
        <AnonymousDialog dialogState={dialog} displayTaxesSwitch />
      )}
    </>
  );
}

PreferencesWidget.preFetch = function preFetch(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  return selectIsAuthenticatedUser(args.getState())
    ? AuthenticatedDialog.preFetch(args)
    : AnonymousDialog.preFetch(args);
};

PreferencesWidget.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<>,
): Promise<void> | Promise<mixed> {
  const { req, res, getState } = args;
  const authenticated = selectIsAuthenticatedUser(getState());

  const { org, ...newQuery } = req.query;
  if (authenticated && org) {
    const sharingId = selectCurrentId(getState());
    const redirectTo = req.originalUrl.replace(sharingId, org).split('?')[0];

    const querystring = Object.keys(newQuery).reduce(
      (acc, param) =>
        `${acc ? `${acc}&` : '?'}${param}=${newQuery[param] || ''}`,
      '',
    );

    res.redirect(redirectTo + querystring);
    return Promise.reject(new Error('redirect to ?org sharing_id'));
  }

  return Promise.resolve();
};
